import { useEffect, useState, type ReactElement } from 'react';

import { isTerminalPhase } from '@harness/stream-core';

import { now } from '../lib/environment.ts';
import type { ChatController } from '../hooks/use-chat.ts';
import { NavTabs } from './nav-tabs.tsx';
import { ScenarioSelect } from './scenario-select.tsx';

const PHASE_TONE: Record<string, string> = {
  thinking: 'badge--signal',
  streaming: 'badge--signal',
  connecting: 'badge--hold',
  queued: 'badge--hold',
  reconnecting: 'badge--hold',
  stalled: 'badge--hold',
  error: 'badge--fault',
};

// TODO: fill in the real repository URL before this goes live.
export const GITHUB_URL = 'https://github.com/{{TODO: github-username}}/chat-stream-harness';

function PlayIcon(): ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5.14v14.72a1 1 0 0 0 1.5.86l12-7.36a1 1 0 0 0 0-1.72l-12-7.36A1 1 0 0 0 8 5.14Z" />
    </svg>
  );
}

function StopIcon(): ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="5" y="5" width="14" height="14" rx="2" />
    </svg>
  );
}

function ClearIcon(): ReactElement {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    </svg>
  );
}

function InspectorIcon(): ReactElement {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M15 3v18" />
    </svg>
  );
}

function GithubIcon(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.09 3.29 9.4 7.86 10.93.58.1.79-.25.79-.56 0-.27-.01-1.17-.02-2.12-3.2.7-3.88-1.36-3.88-1.36-.52-1.34-1.28-1.69-1.28-1.69-1.04-.72.08-.7.08-.7 1.16.08 1.76 1.19 1.76 1.19 1.03 1.75 2.7 1.25 3.36.96.1-.75.4-1.25.73-1.54-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.28 1.19-3.09-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.79 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.11 3.05.74.81 1.19 1.83 1.19 3.09 0 4.42-2.69 5.4-5.25 5.68.41.36.78 1.06.78 2.14 0 1.55-.01 2.79-.01 3.17 0 .31.21.67.8.56A10.52 10.52 0 0 0 23.5 12C23.5 5.65 18.35.5 12 .5Z" />
    </svg>
  );
}

/** Elapsed time since a stream started, ticking once a second while it runs. */
function useElapsed(running: boolean): string | null {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (!running) {
      setElapsedMs(0);
      return;
    }
    const startedAt = now();
    setElapsedMs(0);
    const id = window.setInterval(() => {
      setElapsedMs(now() - startedAt);
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, [running]);

  if (!running) return null;
  const seconds = Math.floor(elapsedMs / 1000);
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

export function Header({
  chat,
  inspectorOpen,
  onToggleInspector,
  route,
  onNavigate,
}: {
  chat?: ChatController;
  /** Whether the inspector panel is open. */
  inspectorOpen?: boolean;
  onToggleInspector?: () => void;
  route: string;
  onNavigate: (path: string) => void;
}): ReactElement {
  const liveState = chat?.liveState ?? null;
  const phase = liveState?.phase ?? 'idle';
  const tone = PHASE_TONE[phase] ?? '';
  const running =
    liveState !== null && liveState.phase !== 'idle' && !isTerminalPhase(liveState.phase);
  const elapsed = useElapsed(running);

  const scenarioOptions =
    chat === undefined
      ? []
      : chat.scenarios.length === 0
        ? [{ id: chat.scenario, proves: '' }]
        : chat.scenarios.map((scenario) => ({ id: scenario.id, proves: scenario.proves }));

  return (
    <header className="header">
      {/* Zone 1: brand and section tabs. */}
      <div className="header__brand-group">
        <h1 className="mono header__name">chat-stream-harness</h1>
        <span className="header__divider" aria-hidden="true" />
        <NavTabs current={route} onNavigate={onNavigate} />
      </div>

      {/* Zone 2: the scenario picker and the run control, the part a reader
          actually operates. */}
      {chat && (
        <div className="header__controls">
          <label className="muted header__label" id="scenario-select-label">
            Scenario
          </label>
          <ScenarioSelect
            labelId="scenario-select-label"
            value={chat.scenario}
            options={scenarioOptions}
            disabled={chat.busy}
            onChange={chat.setScenario}
          />
          {chat.busy ? (
            <button
              type="button"
              className="button button--fault header__action-btn"
              onClick={chat.cancel}
              title="Stop current scenario stream"
            >
              <StopIcon />
              <span>Stop</span>
            </button>
          ) : (
            <button
              type="button"
              className="button button--primary header__action-btn"
              onClick={() => {
                chat.send(`Run the ${chat.scenario} scenario.`);
              }}
              title="Run selected scenario"
            >
              <PlayIcon />
              <span>Run scenario</span>
            </button>
          )}
        </div>
      )}

      {/* Zone 3: status and utilities. */}
      <div className="header__actions-group">
        {chat && (
          <div className="header__actions">
            <button
              type="button"
              className="button button--quiet header__btn-with-icon"
              disabled={chat.turns.length === 0 || chat.busy}
              onClick={chat.clear}
              aria-label="Clear conversation"
              title={chat.turns.length === 0 ? 'No conversation to clear' : 'Clear conversation'}
            >
              <ClearIcon />
              <span>Clear</span>
            </button>
            {onToggleInspector && (
              <button
                type="button"
                className={`button button--quiet header__inspector-toggle header__btn-with-icon${inspectorOpen ? ' header__inspector-toggle--active' : ''}`}
                onClick={onToggleInspector}
                aria-expanded={inspectorOpen === true}
                aria-label="Toggle inspector"
                title={inspectorOpen ? 'Hide inspector' : 'Show inspector'}
              >
                <InspectorIcon />
                <span>Inspector</span>
              </button>
            )}
          </div>
        )}

        <a
          className="header__github"
          href={GITHUB_URL}
          target="_blank"
          rel="noreferrer"
          aria-label="Source on GitHub"
          title="Source on GitHub"
        >
          <GithubIcon />
        </a>

        {chat && (
          <span className="header__status">
            {elapsed !== null && <span className="mono muted header__elapsed">{elapsed}</span>}
            <span className={`badge ${tone} header__badge`}>{phase}</span>
          </span>
        )}
      </div>
    </header>
  );
}
